import {ApplicationRef, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {CommunicationService} from '../../../service/communication.service';
import {DialogViewModelCreatorService} from '../../../service/dialog-view-model-creator.service';
import {BotatoChatManagerService} from '../../../service/botato-chat-manager.service';
import {InputComponent} from '../input.component';
import {VoiceRecognitionService} from './voice/voice-recognition.service';
import {Subscription} from 'rxjs';
import {
    DialogConfigInputOptionsTextInput, DialogConfigInputType
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/DialogConfig/DialogConfig.js';
import {MessageAttachment} from '../../../dto/MessageAttachment';

@Component({
    templateUrl: './text-input.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class TextInputComponent extends InputComponent<DialogConfigInputOptionsTextInput> implements OnInit, OnDestroy {

    public static TYPE: DialogConfigInputType = 'text';
    // Max upload size, kept in sync with the server-side AttachmentPolicy.
    private static readonly MAX_FILE_SIZE_BYTES: number = 10 * 1024 * 1024;
    private static readonly DEFAULT_ACCEPTED_MEDIA_TYPES: string[] = ['*/*'];

    public isConnected: boolean | undefined;
    public inputFocus: boolean | undefined;
    public userText: string = '';
    public addVoiceInput: boolean = false;
    public validateVoiceTextError: boolean = false;
    public waitingForBotResponse: boolean = false;
    // The web widget supports a single attachment, shown as a preview next to the input until sent.
    public selectedAttachment?: MessageAttachment;
    public attachmentError: boolean = false;

    @ViewChild('messageInput', {static: true})
    private messageInput!: ElementRef;
    private subscription = new Subscription();
    private recordedVoice = new Subscription();

    constructor(private communicationService: CommunicationService,
                public dialogViewModelCreator: DialogViewModelCreatorService,
                private chatManagerService: BotatoChatManagerService,
                private applicationRef: ApplicationRef,
                private voiceService: VoiceRecognitionService,
                private changeDetectorRef: ChangeDetectorRef) {
        super();

    }

    public get attachmentsEnabled(): boolean {
        return this.attachmentTextInputOptions?.attachmentsEnabled === true;
    }

    public get attachmentAccept(): string {
        return this.acceptedMediaTypes.join(',');
    }

    private get acceptedMediaTypes(): string[] {
        return this.attachmentTextInputOptions?.attachmentAcceptedTypes ?? TextInputComponent.DEFAULT_ACCEPTED_MEDIA_TYPES;
    }

    private get attachmentTextInputOptions(): (DialogConfigInputOptionsTextInput & { attachmentsEnabled?: boolean; attachmentAcceptedTypes?: string[] }) | undefined {
        return this.inputOptions as (DialogConfigInputOptionsTextInput & { attachmentsEnabled?: boolean; attachmentAcceptedTypes?: string[] }) | undefined;
    }

    public ngOnInit(): void {
        if (this.inputComponentFocus === true) {
            this.messageInput.nativeElement.focus();
        }
        this.setDefaultText();
        this.checkVoiceAvailability();

        this.subscription.add(this.chatManagerService.focusInput$.subscribe(() => this.messageInput.nativeElement.focus()));
        this.subscription.add(this.communicationService.connectionStateChanged.subscribe((state: boolean) => {
            this.isConnected = state;
            this.changeDetectorRef.markForCheck();
        }));
        this.subscription.add(this.chatManagerService.waitingForBotResponse$.subscribe((waiting: boolean) => {
            this.waitingForBotResponse = waiting;
            this.changeDetectorRef.markForCheck();
        }));
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.recordedVoice.unsubscribe();
    }

    public onEnterPressed(event: KeyboardEvent): void {
        if ((event.keyCode === 13 || event.key === 'Enter') && !event.shiftKey) {
            event.preventDefault();

            // It can happen that the keyDown Event of the Enter-key does happen before the keyUp-event of the last
            // key the user typed before hitting the enter button. In this case the binding did not yet refresh
            // the userText and we send an incomplete message. To fix this we add additional 100ms to make sure
            // the event of the last keystroke happened.
            setTimeout(async () => {
                if (this.canSendMessage()) {
                    await this.sendUserText();
                }
            }, 100);
        }
        this.applicationRef.tick();
    }

    public onKeyUp(): void {
        this.applicationRef.tick();
    }

    public async sendUserText(): Promise<void> {
        await this.sendMessage(this.userText);
        this.userText = '';
        this.changeDetectorRef.markForCheck();
        this.applicationRef.tick();
    }

    public canSendMessage(): boolean {
        if (this.waitingForBotResponse || this.isConnected === false || this.validateVoiceText()) {
            return false;
        }
        const hasText = this.userText !== undefined && this.userText.trim().length !== 0;
        if (hasText) {
            return this.validateText(this.userText);
        }
        // An attachment may be sent on its own, without any text.
        return this.selectedAttachment !== undefined;
    }

    public onAttachmentSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = ''; // allow re-selecting the same file after removing it
        if (file === undefined) {
            return;
        }
        this.attachmentError = false;
        if (file.size > TextInputComponent.MAX_FILE_SIZE_BYTES) {
            this.attachmentError = true;
            this.applicationRef.tick();
            return;
        }
        const reader = new FileReader();
        reader.onload = (): void => {
            const result = reader.result as string;
            this.selectedAttachment = {
                name: file.name,
                mediaType: file.type,
                base64Data: result.substring(result.indexOf(',') + 1)
            };
            this.changeDetectorRef.markForCheck();
            this.applicationRef.tick();
        };
        reader.onerror = (): void => {
            this.selectedAttachment = undefined;
            this.attachmentError = true;
            this.changeDetectorRef.markForCheck();
            this.applicationRef.tick();
        };
        reader.readAsDataURL(file);
    }

    public removeAttachment(): void {
        this.selectedAttachment = undefined;
        this.attachmentError = false;
        this.applicationRef.tick();
    }

    public onFocus(): void {
        this.inputFocus = true;
    }

    public validateVoiceText(): boolean {
        if (this.voiceService.finalTranscript.length > 0 && !this.validateText(this.voiceService.finalTranscript)) {
            this.voiceService.tempWords = this.voiceService.finalTranscript;
            this.validateVoiceTextError = true;
            return true;
        }
        return false;
    }

    public validateText(text: string): boolean {

        // Without a configured pattern the expression is empty and matches every text, as new RegExp(undefined) did.
        const regularExpression = new RegExp(this.inputOptions?.validation?.regex ?? '');
        return regularExpression.test(text);
    }

    private async sendMessage(text: string): Promise<void> {
        const attachment = this.selectedAttachment;
        if (text.trim().length > 0 || attachment !== undefined) {
            this.dialogViewModelCreator.addUserTextMessage(text, attachment?.name);
        }
        await this.communicationService.sendMessage(text, 'text', attachment);
        // Clear only after the send call resolves, so the attachment is preserved (and can be retried)
        // if sending throws (e.g. token/connection failure).
        this.selectedAttachment = undefined;
        this.attachmentError = false;
    }

    private setDefaultText(): void {
        const defaultText = this.inputOptions?.defaultText;
        if (defaultText !== undefined) {
            this.userText = defaultText;

            this.messageInput.nativeElement.focus();
        }
    }

    private checkVoiceAvailability(): void {
        if (this.inputOptions?.enableVoice === true) {
            if ('webkitSpeechRecognition' in window) {
                this.addVoiceInput = true;
                console.log('webkitSpeechRecognition exist');
                this.voiceService.init(/Android/i.test(navigator.userAgent));

                this.recordedVoice = this.voiceService.recordedVoice$.subscribe(value => {
                    this.userText = value ? this.voiceService.tempWords : '';
                    this.changeDetectorRef.markForCheck();
                });
                // validateVoiceText() in the template reads the service's transcript, which changes outside of any event
                this.recordedVoice.add(this.voiceService.transcriptChanged$.subscribe(() => this.changeDetectorRef.markForCheck()));
            } else {
                this.addVoiceInput = false;
                console.log('webkitSpeechRecognition not exist');
            }
        }
    }
}
